import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import { Button } from 'components/Button/RoundButton';
import { Form } from 'components/FormComponents/FormComponents';
import { Container, Wrapper } from 'components/Layout/Layout';
import TabSelector from 'components/TabSelector/TabSelector';
import AnchorContent from 'components/AnchorContent/AnchorContent';
import ConfirmModal from 'components/Modals/confirmModal';
import { CompanyCard } from './components/companyCard';
import { EmailTemplate } from './components/emailTemplate';
import { AutoArchiveTests } from './components/autoArchiveTests';
import { Skills } from './components/skills';
import { Plan } from './components/plan';
import { MarketingStatistics } from './components/marketingStatistics';
import RecruitmentStagesSettings from '../SettingsPage/RecruitmentStagesSettings';
import CandidateRatingAreasSettings from '../SettingsPage/CandidateRatingAreasSettings';
import CandidateRatingWeightsSettings from '../SettingsPage/CandidateRatingWeightsSettings';
import CoursePricePlans from './components/CoursePricePlans';
import { Spinner } from 'components/Spinner/Spinner';
import {
  TopHeadingContainer,
  Header,
  SectionTitle,
  CompanyCardContainer,
  CreateCompanyTab,
  OuterContainer,
  TextInput,
  RadioGroup,
  DescriptionQuillContainer,
} from './styles';
import QuillEditor from 'components/QuillEditor/QuillEditorEmail';
import * as actions from './actions';
import * as authActions from 'global/actions/authActions';
import { useActions } from 'hooks/useActions';
import { ErrorText } from './components/styles';
import { RadioButton } from 'components/Checkbox/Checkbox';

export default React.memo(() => {
  const navigate = useNavigate();

  const {
    loadSuperAdmin,
    createCompany,
    updateCompany,
    updateAdminCompany,
    updateEmails,
    updateConfig,
    updatePlan,
    updateCourse,
  } = useActions(actions);
  const { verifyAuthState } = useActions(authActions);

  let {
    error,
    loading,
    companies,
    configEmails,
    boilerPlateEmails,
    updateCompanySuccess,
    companyId,
    updatedUserId,
    areEmailsUpdated,
    archiveTest,
    stages,
    candidateRatings,
    ratingWeight,
    skills,
    paymentPlan,
    updatedPlan,
    updatedConfig,
    courses,
    course,
  } = useSelector((state) => state.SuperAdminPageReducer);

  const [companiesList, setCompanies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [companiesSearched, setCompaniesSearched] = useState([]);

  const [selectedCompany, setSelectedCompany] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTestsOnlyModal, setShowTestsOnlyModal] = useState(false);

  const searchQueryFilter = (searchQuery) => (object) =>
    String(object.name).toLowerCase().indexOf(searchQuery.toLowerCase()) > -1;

  useEffect(() => {
    setCompaniesSearched(companiesList.filter(searchQueryFilter(searchQuery)));
  }, [searchQuery, companiesList]);

  useEffect(() => {
    loadSuperAdmin();
  }, [loadSuperAdmin]);

  useEffect(() => {
    if (error) toast.error((error?.message ?? error?.[0]?.message ?? typeof error === 'string') ? error : error?.[0]);
  }, [error]);

  useEffect(() => {
    setCompanies(companies);
    setCompaniesSearched(companies.filter(searchQueryFilter(searchQuery)));
  }, [companies, searchQuery]);

  useEffect(() => {
    if (updateCompanySuccess) toast.success('Company updated successfully');
  }, [updateCompanySuccess]);

  useEffect(() => {
    if (updateCompanySuccess) toast.success('Company updated successfully');
  }, [updateCompanySuccess]);

  useEffect(() => {
    if (companyId) {
      toast.success('Company created successfully');
      updateAdminCompany(companyId);
    }
  }, [companyId, updateAdminCompany]);

  useEffect(() => {
    if (updatedUserId) {
      verifyAuthState();
      navigate('/tests');
    }
  }, [updatedUserId, verifyAuthState, navigate]);

  useEffect(() => {
    if (areEmailsUpdated) toast.success('Emails updated successfully');
  }, [areEmailsUpdated]);

  useEffect(() => {
    if (updatedConfig._id) toast.success('Settings updated successfully');
  }, [updatedConfig]);

  useEffect(() => {
    if (updatedPlan?._id) toast.success('Plan updated successfully');
  }, [updatedPlan]);

  useEffect(() => {
    if (course._id) toast.success('Course updated successfully');
  }, [course]);

  const CreateCompany = () => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [companyType, setCompanyType] = useState('customer');
    const values = [
      { label: 'Demo Company', value: 'demo' },
      { label: 'Reseller Company', value: 'reseller' },
      { label: 'Customer', value: 'customer' },
    ];
    const [active, setActive] = useState(values[2].value);
    const [displayError, setDisplayError] = useState('');

    return (
      <CreateCompanyTab>
        <Form
          onSubmit={() => {
            if (!name) return setDisplayError('Name is required');
            if (name?.trim?.()?.length < 3) return setDisplayError('Name should be of minimum 3 characters');
            setDisplayError('');
            createCompany({
              name,
              description,
              freeDemo: companyType === 'demo' ? true : false,
              reseller: companyType === 'reseller' ? true : false,
            });
          }}
        >
          <RadioGroup>
            {values.map((v, index) => {
              return (
                <RadioButton
                  key={index}
                  checked={active === v.value}
                  onChange={() => {
                    setActive(v.value);
                    setCompanyType(v.value);
                  }}
                  label={v.label}
                />
              );
            })}
          </RadioGroup>
          <TextInput height="30px" placeholder="Name" onChange={(e) => setName(e.target.value)} value={name} />
          <DescriptionQuillContainer>
            <QuillEditor
              placeholder="Description"
              value={description}
              onChange={(updatedValue) => setDescription(updatedValue)}
            />
          </DescriptionQuillContainer>
          {displayError && (
            <ErrorText marginBottom="10px" marginTop="0px">
              {displayError}
            </ErrorText>
          )}
          <Button type="submit" padding="5px 15px" margin="0" primaryBlue primaryGreenBorder small>
            Add Company
          </Button>
        </Form>
      </CreateCompanyTab>
    );
  };

  const Companies = (
    <OuterContainer>
      <SectionTitle>Companies</SectionTitle>
      <TabSelector
        items={[
          { label: 'Create', component: CreateCompany() },
          {
            label: 'Search',
            component: (
              <TextInput
                margin="10px 10px 20px 10px"
                placeholder="Search"
                onChange={(e) => setSearchQuery(e.target.value)}
                value={searchQuery}
              />
            ),
          },
        ]}
      />
      <CompanyCardContainer>
        {companiesSearched.map((company, index) => {
          return (
            <CompanyCard
              name={company.name || 'No Name'}
              key={index}
              logo={
                company.logo === 'images/placeholder-company.png' ||
                company.logo.includes('images/company/') ||
                company.logo.includes('images/placeholder-company')
                  ? 'https://app.futureskill.com/images/placeholder-company.png'
                  : company.logo
              }
              testsOnly={company.testsOnly}
              deleteFunction={() => {
                setShowDeleteModal(true);
                setSelectedCompany(company);
              }}
              testsOnlyFunction={async function () {
                setShowTestsOnlyModal(true);
                setSelectedCompany(company);
              }}
            />
          );
        })}
      </CompanyCardContainer>
      {showDeleteModal && (
        <ConfirmModal
          title={'Confirmation'}
          text={`Are you sure you want to delete ${selectedCompany.name}?`}
          onConfirm={async function () {
            await updateCompany(selectedCompany._id, { deleted: true });
            setShowDeleteModal(false);
          }}
          onCancel={() => {
            setShowDeleteModal(false);
          }}
        />
      )}
      {showTestsOnlyModal && (
        <ConfirmModal
          title={'Confirmation'}
          text={
            selectedCompany.testsOnly
              ? 'Are you sure you want to remove tests only for this company?'
              : 'Are you sure you want to make this company tests only?'
          }
          onConfirm={async function () {
            await updateCompany(selectedCompany._id, { testsOnly: !selectedCompany.testsOnly });
            setShowTestsOnlyModal(false);
          }}
          onCancel={() => {
            setShowTestsOnlyModal(false);
          }}
        />
      )}
    </OuterContainer>
  );

  const items = [
    { label: 'Companies', icon: 'fa fa-building', component: Companies },
    {
      label: 'Email Template',
      icon: 'fa fa-envelope',
      component: configEmails.length && boilerPlateEmails._id && (
        <EmailTemplate
          configEmails={configEmails}
          boilerPlateConfigEmails={boilerPlateEmails}
          updateEmails={updateEmails}
        />
      ),
    },
    {
      label: 'Tests',
      icon: 'fa fa-file',
      component: archiveTest?._id && <AutoArchiveTests archiveTest={archiveTest} updateConfig={updateConfig} />,
    },
    {
      label: 'Recruitment Stages',
      icon: 'fa fa-arrow-right',
      component: stages?.value && (
        <RecruitmentStagesSettings
          recruitmentStages={stages.value}
          updateStages={updateConfig}
          id={stages._id}
          isSuperadmin={true}
        />
      ),
    },
    {
      label: 'Candidate Ratings',
      icon: 'fa fa-star',
      component: candidateRatings?.value && (
        <CandidateRatingAreasSettings
          candidateRatingAreas={candidateRatings.value}
          updateCandidateRatingAreas={updateConfig}
          id={candidateRatings._id}
          isSuperadmin={true}
        />
      ),
    },
    {
      label: 'Rating Weights',
      icon: 'fa fa-tasks',
      component: ratingWeight?.value && (
        <CandidateRatingWeightsSettings
          candidateRatingWeight={ratingWeight.value}
          updateCandidateRatingWeight={updateConfig}
          id={ratingWeight._id}
          isSuperadmin={true}
        />
      ),
    },
    {
      label: 'Skills',
      icon: 'fa fa-language',
      component: skills?.value && <Skills skills={skills} updateConfig={updateConfig} />,
    },
    {
      label: 'Plans',
      icon: 'fa-regular fa-credit-card',
      component: paymentPlan?._id && <Plan plan={paymentPlan} updatePlan={updatePlan} />,
    },
    {
      label: 'Course Price Plans',
      icon: 'fa fa-book',
      component: paymentPlan?._id && <CoursePricePlans courses={courses} updateCourse={updateCourse} />,
    },
    {
      label: 'Marketing statistics',
      icon: 'fa-solid fa-file-excel',
      component: <MarketingStatistics />,
    },
  ];

  return (
    <Wrapper padding="25px 0 0 0" minHeight="100vh">
      <Container column>
        <TopHeadingContainer>
          <Header>Super Admin</Header>
        </TopHeadingContainer>
        {loading && <Spinner />}
        <AnchorContent anchorPoints={items} hideOnSmallDevice={true}></AnchorContent>
      </Container>
    </Wrapper>
  );
});
